// pages/Help/HelpFaq.jsx
import React, { useEffect, useMemo, useState } from "react";
import HelpAside from "../components/HelpAside";
import axios from "axios";
import { FiSearch } from "react-icons/fi";
import wizMarket from  "../../../assets/image/home/wizMarketLogo.png"

const PAGE_SIZE = 10;

const HelpFaq = () => {
    const [faqList, setFaqList] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);

    // 데이터 로드 (CmsFaq 참고)
    useEffect(() => {
        const fetchFaq = async () => {
            try {
                setLoading(true);
                const url = `${process.env.REACT_APP_FASTAPI_ADS_URL}/faq`;
                const { data } = await axios.get(url);
                setFaqList(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("FAQ 불러오기 실패:", e);
            } finally {
                setLoading(false);
            }
        };
        fetchFaq();
    }, []);

    // 카테고리 목록
    const categories = useMemo(() => {
        const names = new Set(
            faqList
                .map((f) => f.category_name)
                .filter((v) => v !== undefined && v !== null && `${v}`.trim() !== "")
        );
        return ["All", ...Array.from(names)];
    }, [faqList]);

    // 검색/필터 적용
    const filteredFaqs = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        const list = faqList.filter((f) =>
            selectedCategory === "All" ? true : f.category_name === selectedCategory
        );
        if (!q) return list;

        return list.filter((f) => {
            const hay = `${f.category_name ?? ""} ${f.question ?? ""} ${f.answer ?? ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [faqList, selectedCategory, searchText]);

    // 페이지네이션
    const totalPages = Math.max(1, Math.ceil(filteredFaqs.length / PAGE_SIZE));
    const pagedFaqs = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredFaqs.slice(start, start + PAGE_SIZE);
    }, [filteredFaqs, page]);

    // 검색/카테고리 변경 시 1페이지로, 열림 초기화
    useEffect(() => {
        setPage(1);
        setOpenId(null);
    }, [searchText, selectedCategory]);

    const toggleOpen = (id) => {
        setOpenId((prev) => (prev === id ? null : id));
    };

    return (
        <div className="min-h-screen bg-white">
            <header className="max-w-6xl mx-auto top-0 z-50">
                <div className="h-15 px-5 py-3 flex items-center justify-between">
                    <a href="/" className="flex items-center gap-2 pl-6">
                        <img src={wizMarket} alt="wizMarket" className="h-8 w-auto" />
                        <p style={{fontFamily: "SonsieOne"}}>wiz Market</p>
                    </a>
                    <div className="flex items-center gap-2">
                        <a href="/help"className="px-8 py-2 inline-flex items-center rounded-md bg-black/10 text-lg hover:bg-black/30">
                            고객센터
                        </a>
                        {/* <div className="px-4 py-2 inline-flex items-center text-white rounded-md bg-[#7864F9]/60 text-lg font-medium hover:bg-[#7864F9]">
                            로그인/회원가입
                        </div> */}
                    </div>
                </div>
            </header>
            <div className="max-w-6xl mx-auto px-6 grid grid-cols-12 gap-8 pt-8">
                {/* 좌측 사이드 */}
                <HelpAside activeKey="faq" />

                {/* 메인 */}
                <main className="col-span-12 lg:col-span-9">
                    <div className="flex justify-between items-center">
                        <h1 className="text-2xl font-extrabold tracking-tight">자주 찾는 질문</h1>
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="검색어를 입력해주세요"
                                    className="border rounded-md pl-10 pr-4 py-2 text-sm"
                                />
                            </div>
                    </div>

                    {/* 카테고리 필터(있을 때만 표시) */}
                    {categories.length > 1 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-3 py-1.5 rounded-full border text-sm transition ${
                                        selectedCategory === cat
                                            ? "bg-violet-600 text-white border-violet-600"
                                            : "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200"
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    )}

                    <p className="text-gray-500 pt-3 pb-6">
                        총 <span className="font-semibold">{filteredFaqs.length}</span> 건
                    </p>

                    {/* 리스트 */}
                    <div className="rounded-2xl border divide-y">
                        {!loading && pagedFaqs.length === 0 ? (
                            <div className="px-4 py-10 text-center text-gray-400">FAQ가 없습니다.</div>
                        ) : (
                            pagedFaqs.map((faq) => (
                                <div
                                    key={faq.faq_id}
                                    className="px-4 py-4 cursor-pointer"
                                    onClick={() => toggleOpen(faq.faq_id)}
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-violet-50 text-violet-600 text-xs font-bold">
                                                Q
                                            </span>
                                            {faq.category_name && (
                                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                                    {faq.category_name}
                                                </span>
                                            )}
                                            <span className="text-gray-800">{faq.question}</span>
                                        </div>
                                        <span className="text-gray-400">
                                            {openId === faq.faq_id ? "▲" : "▼"}
                                        </span>
                                    </div>

                                    {openId === faq.faq_id && (
                                        <div className="w-full mt-3 pl-8 text-sm text-gray-600 whitespace-pre-line">
                                            {faq.answer}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {/* 페이지네이션 */}
                    {totalPages > 1 && (
                        <div className="flex justify-center gap-4 mt-6">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                                <button
                                    key={num}
                                    onClick={() => setPage(num)}
                                    className={`w-8 h-8 rounded-full ${
                                        num === page
                                            ? "bg-violet-500 text-white"
                                            : "text-gray-700 hover:bg-gray-100"
                                    }`}
                                >
                                    {num}
                                </button>
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default HelpFaq;
